import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  connectAdminTrafficSseStream,
  getTrafficEventsStrict,
  getTrafficInspectorMode,
  getTrafficStreamSseUrl,
  isPlainHttpTrafficOrigin,
  isVisibleTrafficInspectorEvent,
  marchSseToTrafficEvent,
  normalizeTrafficStreamEventPayload,
} from '../api/trafficInspectorApi';
import type {
  TrafficInspectorEvent,
  TrafficInspectorNode,
  TrafficInspectorEdge,
  TrafficTopologyPayload,
} from '../types/trafficInspector';
import { shouldAcceptPollResult } from '../utils/pollSequence';
import { isValidTopology } from '../utils/trafficTopologyState';
import { MARCH_EVENT_STORAGE_KEY, purgePersistedTrafficEvents } from './trafficEventStorage';

/**
 * Newest-N events the inspector retains in memory.
 *
 * #112: exported so the UI can say "showing the newest 1000" instead of
 * rendering the number as though it were a total. A viewer who reads "1000
 * events" has no way to tell a genuine count from a saturated buffer — the
 * question this ticket came from was exactly that.
 */
export const MAX_EVENTS = 1000;
const FALLBACK_HISTORY_POLL_MS = 60_000;
const FALLBACK_HISTORY_MAX_BACKOFF_MS = 300_000;
const RECONNECT_INITIAL_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
// #113: consecutive SSE attempts that never went live before auto-reconnect
// gives up for the session. Each stale attempt holds a browser socket for the
// full liveness window (plain-HTTP buffering, #145), so retrying forever kept
// the admin origin's connection budget permanently taxed. History polling
// keeps the page fully functional; the Reconnect button still retries by hand.
const MAX_CONSECUTIVE_SSE_FAILURES = 3;
const SSE_GIVE_UP_MESSAGE =
  'Live traffic stream is unavailable on this connection (no events arrive over plain HTTP). '
  + 'Using periodic history refresh instead — data below stays current, just not instant.';

const spanSeq = { next: 0 };

function eventStorageKey(event: TrafficInspectorEvent): string {
  return event.id
    || [
      event.traceId,
      event.spanId,
      event.timestamp,
      event.source,
      event.destination,
      event.eventName,
      event.summary,
    ].join('|');
}

function mergeEvents(primary: TrafficInspectorEvent[], secondary: TrafficInspectorEvent[]): TrafficInspectorEvent[] {
  const merged: TrafficInspectorEvent[] = [];
  const seen = new Set<string>();
  for (const event of [...primary, ...secondary]) {
    if (!isVisibleTrafficInspectorEvent(event.eventName)) continue;
    const key = eventStorageKey(event);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(event);
    if (merged.length >= MAX_EVENTS) break;
  }
  return merged
    .slice()
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, MAX_EVENTS);
}

function readPersistedMarchEvents(): TrafficInspectorEvent[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(MARCH_EVENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? (parsed as TrafficInspectorEvent[]).filter((event) => isVisibleTrafficInspectorEvent(event.eventName))
      : [];
  } catch {
    return [];
  }
}

/**
 * #112 (2026-08-01): a byte budget for the persisted feed.
 *
 * The store was bounded by COUNT alone (MAX_EVENTS), and event size varies by
 * orders of magnitude — a status ping is a few hundred bytes, a chart tool
 * result is hundreds of kilobytes — so a count cap places no bound at all on
 * memory. Measured live this session: 1000 persisted events reached
 * 19,942,848 characters (~20 MB) in localStorage, and opening the Traffic
 * Inspector froze the tab for over 90 seconds before the renderer crashed the
 * whole session. (#101 capped tool-result payloads inside the live feed; this
 * is the persistence half of the same problem, which #101 did not reach.)
 *
 * 4 MB is chosen against the ~5 MB per-origin localStorage quota browsers
 * enforce: it leaves headroom for every other key the app stores, and it is
 * far below the size that made the page unusable.
 */
const MAX_PERSISTED_BYTES = 4_000_000;

/**
 * Newest-first events that fit inside the byte budget.
 *
 * Trimming from the NEWEST end down is what makes this safe: the most recent
 * events are the ones an operator is looking for, so a budget overrun drops the
 * oldest history rather than the request that just ran. Exported for tests.
 */
export function eventsWithinByteBudget(
  events: TrafficInspectorEvent[],
  maxBytes: number = MAX_PERSISTED_BYTES,
): TrafficInspectorEvent[] {
  const kept: TrafficInspectorEvent[] = [];
  // 2 accounts for the enclosing "[]" of the serialized array.
  let bytes = 2;
  for (const event of events) {
    // +1 for the "," separator between elements.
    const size = JSON.stringify(event).length + 1;
    if (kept.length > 0 && bytes + size > maxBytes) break;
    kept.push(event);
    bytes += size;
  }
  return kept;
}

function writePersistedMarchEvents(events: TrafficInspectorEvent[]): void {
  if (typeof window === 'undefined') return;
  try {
    const bounded = eventsWithinByteBudget(events.slice(0, MAX_EVENTS));
    window.localStorage.setItem(MARCH_EVENT_STORAGE_KEY, JSON.stringify(bounded));
  } catch {
    // Ignore storage quota / privacy errors.
  }
}

function isDocumentHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden;
}

export interface TrafficStreamContextValue {
  events: TrafficInspectorEvent[];
  nodes: TrafficInspectorNode[];
  edges: TrafficInspectorEdge[];
  status: 'disconnected' | 'connecting' | 'connected' | 'error';
  error: string | null;
  paused: boolean;
  setPaused: (paused: boolean) => void;
  lastTopologyAt: string | null;
  lastStreamAt: string | null;
  streamTransport: 'sse' | 'fallback' | 'none';
  /** `force: true` (manual Reconnect) bypasses the plain-HTTP zero-attempt skip below. */
  connect: (options?: { force?: boolean }) => void;
  disconnect: () => void;
  clearEvents: () => void;
  /** Push March chat SSE event into the Request stream (March mode only). */
  pushMarchSseEvent: (payload: { channel?: string; event?: string; data?: Record<string, unknown>; id?: number }, runId: string) => void;
}

const TrafficStreamContext = createContext<TrafficStreamContextValue | null>(null);

export function TrafficStreamProvider({ children }: { children: React.ReactNode }) {
  const [events, setEvents] = useState<TrafficInspectorEvent[]>(() => readPersistedMarchEvents());
  const [nodes, setNodes] = useState<TrafficInspectorNode[]>([]);
  const [edges, setEdges] = useState<TrafficInspectorEdge[]>([]);
  const [status, setStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [lastTopologyAt, setLastTopologyAt] = useState<string | null>(null);
  const [lastStreamAt, setLastStreamAt] = useState<string | null>(null);
  const [streamTransport, setStreamTransport] = useState<'sse' | 'fallback' | 'none'>('none');
  const streamAbortRef = useRef<(() => void) | null>(null);
  const pausedRef = useRef(false);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const historyRefreshInFlightRef = useRef(false);
  const historyRefreshSeqRef = useRef(0);
  const fallbackBackoffMsRef = useRef(FALLBACK_HISTORY_POLL_MS);
  const reconnectBackoffMsRef = useRef(RECONNECT_INITIAL_MS);
  const sseFailureCountRef = useRef(0);
  const mountedRef = useRef(true);
  pausedRef.current = paused;

  const stopFallbackPolling = useCallback(() => {
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
  }, []);

  const mergeIncomingEvents = useCallback((incoming: TrafficInspectorEvent[]) => {
    if (incoming.length === 0 || pausedRef.current) return;
    setEvents((prev) => {
      const merged = mergeEvents(incoming, mergeEvents(prev, readPersistedMarchEvents()));
      writePersistedMarchEvents(merged);
      return merged;
    });
  }, []);

  const refreshHistorySnapshot = useCallback(async ({ skipIfHidden = false }: { skipIfHidden?: boolean } = {}) => {
    if (getTrafficInspectorMode() === 'none') return;
    if ((skipIfHidden && isDocumentHidden()) || pausedRef.current) return;
    if (historyRefreshInFlightRef.current) return;
    historyRefreshInFlightRef.current = true;
    const seq = ++historyRefreshSeqRef.current;
    try {
      const historical = await getTrafficEventsStrict({ limit: MAX_EVENTS });
      if (!mountedRef.current || !shouldAcceptPollResult(seq, historyRefreshSeqRef.current)) return;
      mergeIncomingEvents(historical);
    } finally {
      if (seq === historyRefreshSeqRef.current) {
        historyRefreshInFlightRef.current = false;
      }
    }
  }, [mergeIncomingEvents]);

  const scheduleFallbackHistoryRefresh = useCallback(() => {
    stopFallbackPolling();
    if (!mountedRef.current || getTrafficInspectorMode() === 'none') return;
    setStreamTransport('fallback');
    const tick = async () => {
      if (!mountedRef.current) return;
      try {
        await refreshHistorySnapshot({ skipIfHidden: true });
        fallbackBackoffMsRef.current = FALLBACK_HISTORY_POLL_MS;
      } catch {
        fallbackBackoffMsRef.current = Math.min(
          fallbackBackoffMsRef.current * 2,
          FALLBACK_HISTORY_MAX_BACKOFF_MS
        );
      } finally {
        if (mountedRef.current && streamAbortRef.current == null) {
          fallbackTimeoutRef.current = setTimeout(tick, fallbackBackoffMsRef.current);
        }
      }
    };
    fallbackTimeoutRef.current = setTimeout(tick, fallbackBackoffMsRef.current);
  }, [refreshHistorySnapshot, stopFallbackPolling]);

  const handleTopologyPayload = useCallback((payload: unknown) => {
    if (!payload || typeof payload !== 'object') return;
    const topology = payload as Partial<TrafficTopologyPayload>;
    if (!Array.isArray(topology.nodes) || !Array.isArray(topology.edges)) return;
    const candidate = { nodes: topology.nodes, edges: topology.edges };
    if (!isValidTopology(candidate)) return;
    setNodes((current) => {
      if (current.length === 0) return candidate.nodes;
      const nextIds = candidate.nodes.map((node) => node.id).sort().join('|');
      const currentIds = current.map((node) => node.id).sort().join('|');
      return nextIds === currentIds ? current : candidate.nodes;
    });
    setEdges((current) => {
      if (current.length === 0) return candidate.edges;
      const nextIds = candidate.edges
        .map((edge) => edge.edgeId ?? `${edge.source}->${edge.target}`)
        .sort()
        .join('|');
      const currentIds = current
        .map((edge) => edge.edgeId ?? `${edge.source}->${edge.target}`)
        .sort()
        .join('|');
      return nextIds === currentIds ? current : candidate.edges;
    });
    setLastTopologyAt(topology.updatedAt ?? new Date().toISOString());
  }, []);

  const handleStreamTrafficPayload = useCallback((payload: unknown) => {
    const ev = normalizeTrafficStreamEventPayload(payload);
    if (!ev) return;
    mergeIncomingEvents([ev]);
  }, [mergeIncomingEvents]);

  const connect = useCallback((options?: { force?: boolean }) => {
    if (streamAbortRef.current != null) return;
    if (getTrafficInspectorMode() === 'none') {
      setStatus('disconnected');
      setStreamTransport('none');
      setError('Traffic stream URL not configured. Set VITE_MARCH_API_URL or VITE_COMMAND_API_URL.');
      return;
    }
    // #113-hardening: plain-HTTP origins can never receive an SSE event (the
    // fetch is buffered to completion, #145) — skip straight to the polling
    // fallback with zero connection attempts. Manual Reconnect (force: true)
    // still gets to try once, in case the origin's transport behavior changed.
    if (!options?.force && isPlainHttpTrafficOrigin()) {
      setStatus('disconnected');
      setStreamTransport('fallback');
      setError(SSE_GIVE_UP_MESSAGE);
      scheduleFallbackHistoryRefresh();
      return;
    }
    const url = getTrafficStreamSseUrl();
    if (!url) {
      setStatus('disconnected');
      setStreamTransport('fallback');
      setError('Traffic stream URL not configured. Set VITE_MARCH_API_URL or VITE_COMMAND_API_URL.');
      scheduleFallbackHistoryRefresh();
      return;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    stopFallbackPolling();
    setStatus('connecting');
    setStreamTransport('sse');
    setError(null);
    void refreshHistorySnapshot();

    const markLive = () => {
      setStatus('connected');
      setStreamTransport('sse');
      setLastStreamAt(new Date().toISOString());
      reconnectBackoffMsRef.current = RECONNECT_INITIAL_MS;
      fallbackBackoffMsRef.current = FALLBACK_HISTORY_POLL_MS;
      sseFailureCountRef.current = 0;
      setError(null);
    };

    const parseData = (data: unknown): unknown => {
      if (data == null) return null;
      return data;
    };

    const onTrafficData = (data: unknown) => {
      try {
        markLive();
        handleStreamTrafficPayload(parseData(data));
      } catch (parseError) {
        setError(
          parseError instanceof Error
            ? `Traffic stream parse error: ${parseError.message}`
            : 'Traffic stream parse error',
        );
      }
    };

    const onTopologyData = (data: unknown) => {
      try {
        markLive();
        handleTopologyPayload(parseData(data));
      } catch (parseError) {
        setError(
          parseError instanceof Error
            ? `Topology stream parse error: ${parseError.message}`
            : 'Topology stream parse error',
        );
      }
    };

    const scheduleReconnect = () => {
      if (!mountedRef.current || !getTrafficStreamSseUrl()) return;
      sseFailureCountRef.current += 1;
      if (sseFailureCountRef.current >= MAX_CONSECUTIVE_SSE_FAILURES) {
        // #113: stop burning connection slots on a transport that never goes
        // live here. Fallback polling (scheduled by the caller) carries the
        // page from now on; a manual Reconnect retries SSE deliberately.
        setError(SSE_GIVE_UP_MESSAGE);
        return;
      }
      const delay = reconnectBackoffMsRef.current;
      reconnectBackoffMsRef.current = Math.min(reconnectBackoffMsRef.current * 2, RECONNECT_MAX_MS);
      reconnectTimeoutRef.current = setTimeout(() => connect(), delay);
    };

    try {
      const abort = connectAdminTrafficSseStream({
        onOpen: markLive,
        onMeta: markLive,
        onTrafficPayload: onTrafficData,
        onTopologyPayload: onTopologyData,
        onStreamWarning: (message) => {
          markLive();
          setError(message);
        },
        onFetchError: (status, message) => {
          streamAbortRef.current = null;
          setStatus(status === 401 || status === 403 ? 'error' : 'disconnected');
          setStreamTransport('fallback');
          setError(
            status === 401 || status === 403
              ? 'You do not have permission to access this resource.'
              : message || 'Live traffic stream unavailable.',
          );
          scheduleFallbackHistoryRefresh();
        },
        onDisconnected: () => {
          if (streamAbortRef.current == null) return;
          streamAbortRef.current = null;
          setStatus('disconnected');
          setStreamTransport('fallback');
          setError('Live traffic stream disconnected. Reconnecting; using slow history refresh as a fallback.');
          scheduleFallbackHistoryRefresh();
          scheduleReconnect();
        },
      });
      streamAbortRef.current = abort;
    } catch (e) {
      setStatus('disconnected');
      setStreamTransport('fallback');
      setError(e instanceof Error ? e.message : 'Failed to connect');
      scheduleFallbackHistoryRefresh();
      scheduleReconnect();
    }
  }, [
    handleStreamTrafficPayload,
    handleTopologyPayload,
    refreshHistorySnapshot,
    scheduleFallbackHistoryRefresh,
    stopFallbackPolling,
  ]);

  const disconnect = useCallback(() => {
    stopFallbackPolling();
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (streamAbortRef.current) {
      streamAbortRef.current();
      streamAbortRef.current = null;
    }
    setStatus('disconnected');
    setStreamTransport('none');
    setError(null);
  }, [stopFallbackPolling]);

  const clearPersistedEvents = useCallback(() => {
    purgePersistedTrafficEvents();
  }, []);

  const clearEvents = useCallback(() => {
    clearPersistedEvents();
    setEvents([]);
  }, [clearPersistedEvents]);

  const pushMarchSseEvent = useCallback(
    (payload: { channel?: string; event?: string; data?: Record<string, unknown>; id?: number }, runId: string) => {
      if (getTrafficInspectorMode() !== 'march') return;
      if (pausedRef.current) return;
      const ev = marchSseToTrafficEvent(payload, runId, spanSeq);
      if (ev) {
        mergeIncomingEvents([ev]);
      }
    },
    [mergeIncomingEvents]
  );

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [disconnect]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== MARCH_EVENT_STORAGE_KEY) return;
      setEvents((prev) => mergeEvents(readPersistedMarchEvents(), prev));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value: TrafficStreamContextValue = {
    events,
    nodes,
    edges,
    status,
    error,
    paused,
    setPaused,
    lastTopologyAt,
    lastStreamAt,
    streamTransport,
    connect,
    disconnect,
    clearEvents,
    pushMarchSseEvent,
  };

  return (
    <TrafficStreamContext.Provider value={value}>
      {children}
    </TrafficStreamContext.Provider>
  );
}

export function useTrafficStream(): TrafficStreamContextValue {
  const ctx = useContext(TrafficStreamContext);
  if (!ctx) throw new Error('useTrafficStream must be used within TrafficStreamProvider');
  return ctx;
}

export function useTrafficStreamOptional(): TrafficStreamContextValue | null {
  return useContext(TrafficStreamContext);
}
